import { QueryObserverResult } from "@tanstack/react-query"
import { Uuid } from "helpers/types/uuid"
import { useGetQuery } from "hooks/queries"
import { produce } from "immer"
import { createContext, ReactNode, useCallback, useEffect, useMemo, useReducer } from "react"
import { useAccountAlias } from "resources/account"
import { DeepReadonly } from "ts-essentials"
import { usePreferences } from "hooks/use-preferences"
import DeleteConfirmDialog from "../components/delete-confirm-dialog"
import { useDeleteThread } from "../hooks/delete-thread"
import { useUpdateThread } from "../hooks/update-thread"
import { FetchThreadsResponse, Thread } from "../types/thread"
import { useAiTeammateContext } from "../hooks/ai-teammate-context"
import { filterThreadsByAgentType } from "../utils/filter-threads"
import { getThreadsQueryKey } from "../utils/threads-cache"

const PREFERENCE_SUBTYPE = "ai-teammate-thread"
const PREFERENCE_FIELDS = [
  "threadId",
  "threadTitle",
  "isThreadOpen",
]

interface ThreadsState {
  threadId: Uuid | null
  threadTitle: string | null
  isThreadOpen: boolean
  threadIdToDelete: Uuid | null
}

type ThreadsAction = {
  type: "openThread"
  threadId: Uuid
  threadTitle: string
} | {
  type: "newThread"
} | {
  type: "closeThread"
} | {
  type: "setThreadIdToDelete"
  threadIdToDelete: Uuid | null
} | {
  type: "setPreferences"
  preferences: Partial<ThreadsState>
}

const DEFAULT_STATE: ThreadsState = {
  threadId: null,
  threadTitle: null,
  isThreadOpen: false,
  threadIdToDelete: null,
}

function reducer(current: ThreadsState, action: ThreadsAction): ThreadsState {
  return produce(current, draft => {
    switch (action.type) {
      case "openThread":
        draft.threadId = action.threadId
        draft.threadTitle = action.threadTitle
        draft.isThreadOpen = true
        break
      case "newThread":
        draft.threadId = null
        draft.threadTitle = "New Chat"
        draft.isThreadOpen = true
        break
      case "closeThread":
        draft.threadId = null
        draft.threadTitle = null
        draft.isThreadOpen = false
        break
      case "setThreadIdToDelete":
        draft.threadIdToDelete = action.threadIdToDelete
        break
      case "setPreferences":
        Object.assign(draft, action.preferences)
        break
    }
  })
}

function openThread(threadId: Uuid, threadTitle: string) {
  return { type: "openThread" as const, threadId, threadTitle }
}

function newThread() {
  return { type: "newThread" as const }
}

function closeThread() {
  return { type: "closeThread" as const }
}

function setThreadIdToDelete(threadIdToDelete: Uuid | null) {
  return { type: "setThreadIdToDelete" as const, threadIdToDelete }
}

function setPreferences(preferences: Partial<ThreadsState>) {
  return { type: "setPreferences" as const, preferences }
}

export interface ThreadsContextUpdaters {
  openThread: (threadId: Uuid, threadTitle: string) => void
  newThread: () => void
  closeThread: () => void
  setThreadIdToDelete: (threadIdToDelete: Uuid | null) => void
}

export interface ThreadsContextValue extends ThreadsState, ThreadsContextUpdaters {
  threads: Thread[]
  isFetching: boolean
  // True once the threads list has loaded successfully — lets consumers tell "still loading" apart
  // from "thread genuinely absent from the list".
  isThreadsLoaded: boolean
  refetch: () => Promise<QueryObserverResult<FetchThreadsResponse, Error>>
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ThreadsContext = createContext<DeepReadonly<ThreadsContextValue>>(null!)

interface ThreadsProviderProps {
  children: ReactNode
  persistPreferences?: boolean
}

// Once the thread list has loaded, drop the active thread if it is no longer in the list — it was
// deleted (possibly from another surface that shares this account's threads). Without this, restoring
// a persisted-but-deleted thread fetches its messages and fails. Returns the active thread unchanged
// while the list is still loading or when no thread is open.
export function resolveActiveThread(
  state: Pick<DeepReadonly<ThreadsState>, "threadId" | "threadTitle" | "isThreadOpen">,
  threads: readonly Pick<Thread, "threadId">[],
  threadsLoaded: boolean,
): Pick<ThreadsState, "threadId" | "threadTitle" | "isThreadOpen"> {
  const activeThreadDeleted = threadsLoaded
    && state.threadId != null
    && !threads.some((thread) => thread.threadId === state.threadId)

  if (activeThreadDeleted) {
    return { threadId: null, threadTitle: null, isThreadOpen: false }
  }
  return { threadId: state.threadId, threadTitle: state.threadTitle, isThreadOpen: state.isThreadOpen }
}

export function ThreadsProvider({ children, persistPreferences = true }: DeepReadonly<ThreadsProviderProps>) {
  const accountAlias = useAccountAlias()
  const { isManagedAgent, setError } = useAiTeammateContext()
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
    { disabled: !persistPreferences },
  )

  const { data, isFetching, isSuccess, refetch } = useGetQuery<FetchThreadsResponse>({
    key: getThreadsQueryKey(accountAlias),
    url: `/api/${accountAlias}/ai/chat/threads`,
  })

  const { deleteThread: deleteThreadMutation } = useDeleteThread()

  const { updateThread, isUpdatingThread } = useUpdateThread()

  const contextUpdaters = useMemo(() => ({
    openThread: (threadId: Uuid, threadTitle: string) => dispatch(openThread(threadId, threadTitle)),
    newThread: () => dispatch(newThread()),
    closeThread: () => dispatch(closeThread()),
    setThreadIdToDelete: (threadIdToDelete: Uuid | null) => dispatch(setThreadIdToDelete(threadIdToDelete)),
  }), [dispatch])

  // Fire-and-forget: close the dialog and leave the deleted thread now rather than waiting on a DELETE that
  // can take minutes in production. The mutation restores the row and notifies if the request fails.
  const handleDeleteThread = useCallback(() => {
    const threadId = state.threadIdToDelete
    if (!threadId) {
      return
    }
    deleteThreadMutation({ accountAlias, threadId })
    if (state.threadId === threadId && state.isThreadOpen) {
      contextUpdaters.newThread()
    }
    contextUpdaters.setThreadIdToDelete(null)
  }, [deleteThreadMutation, accountAlias, state.threadId, state.threadIdToDelete, state.isThreadOpen, contextUpdaters])

  const threads = useMemo(() => filterThreadsByAgentType(data?.threads || [], isManagedAgent), [data, isManagedAgent])
  const hasThreads = useMemo(() => threads.length > 0, [threads])

  const activeThread = useMemo(() => resolveActiveThread(state, threads, isSuccess), [state, threads, isSuccess])

  // resolveActiveThread only masks a deleted thread in the context value; reconcile the reducer too so the
  // stale id stops being persisted and any fetch error it raised while the list was loading gets cleared.
  const activeThreadDropped = activeThread.threadId !== state.threadId
  useEffect(() => {
    if (activeThreadDropped) {
      dispatch(closeThread())
      setError(null)
    }
  }, [activeThreadDropped, setError])

  const value = useMemo<ThreadsContextValue>(() => ({
    ...state,
    ...contextUpdaters,
    ...activeThread,
    isThreadOpen: activeThread.isThreadOpen || !hasThreads,
    threads,
    hasThreads,
    isFetching,
    isThreadsLoaded: isSuccess,
    refetch,
    updateThread,
    isUpdatingThread,
    isPreferencesLoaded,
  }), [state, contextUpdaters, activeThread, threads, hasThreads, isFetching, isSuccess, refetch, updateThread, isUpdatingThread, isPreferencesLoaded])

  return <ThreadsContext.Provider value={value}>
    {children}
    <DeleteConfirmDialog open={!!state.threadIdToDelete} onClose={() => contextUpdaters.setThreadIdToDelete(null)} onConfirm={handleDeleteThread} />
  </ThreadsContext.Provider>
}
